import { ArrowRight, Flame, Search, AlertTriangle } from "lucide-react";
import { Link } from "react-router-dom";
import { useDashboard } from "../context/DashboardContext";
import { pacingLabel } from "../lib/format";
import type { ClientSummary } from "../types";

function ClientRow({ client }: { client: ClientSummary }) {
  const { selectedClient, setSelectedClient } = useDashboard();
  const isSelected = selectedClient === client.clientSlug;
  const percent = Math.min(Math.max(client.pacingPct * 100, 0), 100);
  const tone =
    client.status === "OVER"
      ? "danger"
      : client.status === "AT_RISK"
        ? "warning"
        : "good";
  const isBudgetDeviated =
    client.budgetMonthEst > 0 &&
    (client.budgetPlanMonthEst ?? 0) > 0 &&
    client.budgetMonthEst > client.budgetPlanMonthEst!;

  return (
    <article className={isSelected ? "client-row is-selected" : "client-row"}>
      <button
        className="client-row-main"
        type="button"
        onClick={() => setSelectedClient(isSelected ? "ALL" : client.clientSlug)}
        aria-pressed={isSelected}
      >
        <div className="client-row-title">
          <span className={`status-beacon tone-${tone}`} />
          <strong>{client.displayName}</strong>
          {client.criticalCount > 0 && (
            <span className="fire-badge" title={`${client.criticalCount} alertas críticas`}>
              {client.criticalCount} <Flame size={11} />
            </span>
          )}
          {isBudgetDeviated && (
            <span className="fire-badge text-danger" title="Presupuesto configurado > Planificado" style={{ marginLeft: '4px', background: 'var(--bg-elevated)', border: '1px solid var(--border-danger)' }}>
              <AlertTriangle size={11} />
            </span>
          )}
        </div>
        <div className="pacing-track" aria-label={`${Math.round(client.pacingPct * 100)}% del presupuesto`}>
          <span className={`tone-${tone}`} style={{ width: `${percent}%` }} />
        </div>
        <div className="client-row-meta">
          <span>{Math.round(client.pacingPct * 100)}%</span>
          <strong className={`text-${tone}`}>
            {client.budgetMonthEst > 0
              ? pacingLabel(client.status, client.pacingPct)
              : "SIN INVERSIÓN"}
          </strong>
        </div>
      </button>
      <button
        className="icon-button"
        type="button"
        aria-label={`Filtrar alertas de ${client.displayName}`}
        onClick={() => setSelectedClient(isSelected ? "ALL" : client.clientSlug)}
      >
        <Search size={15} />
      </button>
      <Link
        className="icon-button"
        to="/cliente"
        aria-label={`Abrir cliente ${client.displayName}`}
        onClick={() => setSelectedClient(client.clientSlug)}
      >
        <ArrowRight size={15} />
      </Link>
    </article>
  );
}

export function ClientRail({ clients }: { clients: ClientSummary[] }) {
  const { selectedClient, setSelectedClient } = useDashboard();
  const sorted = [...clients].sort((a, b) => {
    const activeBoostA = a.clientSlug === selectedClient ? 10 : 0;
    const activeBoostB = b.clientSlug === selectedClient ? 10 : 0;
    return activeBoostB - activeBoostA || b.pacingPct - a.pacingPct;
  });

  return (
    <section className="panel client-rail">
      <div className="panel-heading">
        <div>
          <span className="eyebrow blue">Cartera activa</span>
          <h2>Ritmo de inversión</h2>
        </div>
        <span className="count-badge">{clients.length}</span>
      </div>
      {selectedClient !== "ALL" && (
        <button className="clear-filter" type="button" onClick={() => setSelectedClient("ALL")}>
          Mostrar toda la cartera
        </button>
      )}
      <div className="client-list">
        {sorted.map((client, index) => (
          <div className="enter" style={{ animationDelay: `${80 + index * 28}ms` }} key={client.clientSlug}>
            <ClientRow client={client} />
          </div>
        ))}
      </div>
    </section>
  );
}
